/**
 * Navigation Configuration - Compliance Operating System
 * 
 * @description Defines the navigation structure for the Disclosure Operating System.
 * This architecture follows an "operating system" model, not a feature catalog.
 * 
 * Key Design Principles:
 * 1. DISCLOSURE CYCLES as primary workflow - "what am I working on?"
 * 2. FRAMEWORKS as regulation library - "what are the requirements?"
 * 3. EVIDENCE HUB as single source of truth - "upload once, use everywhere"
 * 4. OBLIGATIONS ENGINE as task driver - "what do I owe today?"
 * 5. AUDIT & ASSURANCE for defensibility - "how do I prove this?"
 * 
 * The Defensibility Flywheel (Upload → Map → Evidence → Claim → Audit) runs
 * INSIDE Disclosure Cycles, visible as phase indicators on each cycle.
 * 
 * @version 2.0.0 - Compliance Operating System Architecture
 * @author Disclosure OS Team
 */

import {
  // Primary Navigation
  FileText,
  Calendar,
  CheckCircle2,
  Clock,
  
  // Frameworks
  Scale,
  Leaf,
  Globe,
  Users,
  Target,
  TrendingUp,
  Landmark,
  Droplets,
  TreePine,
  HardHat,
  ShoppingCart,
  Thermometer,
  SlidersHorizontal,
  Recycle,
  Gavel,
  
  // Evidence Hub
  Upload,
  FolderOpen,
  GitMerge,
  UserCheck,
  FileSearch,
  
  // Obligations Engine
  Zap,
  ClipboardList,
  AlertTriangle,
  Timer,
  
  // Audit & Assurance
  Shield,
  Package,
  CheckSquare,
  Link2,
  
  // Value Chain
  Factory,
  FileQuestion,
  Layers,
  MessageSquare,
  
  // Intelligence
  BarChart3,
  Brain,
  LayoutDashboard,
  AlertOctagon,
  Radar,
  ShieldCheck,
  ScanSearch,
  
  // Reports
  FileBarChart,
  Download,
  
  // System
  Settings,
  Lock,
  Building2,
  ListChecks,
  HelpCircle,

  // Admin
  Activity,
  ScrollText,
  Gauge,
  MonitorCheck,

  // Simulations
  FlaskConical,
  GitCompareArrows,
  Sparkles,
  Cpu,
  
  // Wired Hub
  Plug,
} from 'lucide-react';

/**
 * Navigation section types
 */
export const NavSectionType = {
  PRIMARY: 'PRIMARY',         // Hero section (Disclosure Cycles)
  MATERIALITY: 'MATERIALITY', // Upstream intelligence (Materiality)
  LIBRARY: 'LIBRARY',         // Reference sections (Frameworks)
  WORKFLOW: 'WORKFLOW',       // Execution sections (Evidence, Obligations)
  ASSURANCE: 'ASSURANCE',     // Defensibility (Audit)
  EXTERNAL: 'EXTERNAL',       // External parties (Value Chain)
  INSIGHTS: 'INSIGHTS',       // Analytics (Intelligence)
  OUTPUT: 'OUTPUT',           // Outputs (Reports)
  CONFIG: 'CONFIG',           // Configuration (System)
  ADMIN: 'ADMIN',             // Admin (Internal employees only)
};

/**
 * Badge types for navigation items
 */
export const BadgeType = {
  COUNT: 'COUNT',       // Numeric count (e.g., "3" active cycles)
  PHASE: 'PHASE',       // Phase indicator (e.g., "Mapping")
  NEW: 'NEW',           // New feature
  AI: 'AI',             // AI-powered
  URGENT: 'URGENT',     // Needs attention
  BETA: 'BETA',         // Beta feature
};

/**
 * Disclosure cycle phases (Defensibility Flywheel)
 */
export const CyclePhase = {
  UPLOAD: { id: 'upload', label: 'Upload', color: 'emerald' },
  MAPPING: { id: 'mapping', label: 'Mapping', color: 'blue' },
  EVIDENCE: { id: 'evidence', label: 'Evidence Review', color: 'purple' },
  AUDIT: { id: 'audit', label: 'Audit Trail', color: 'amber' },
  REPORT: { id: 'report', label: 'Report', color: 'cyan' },
};

/**
 * Main navigation configuration
 * 
 * Structure follows the Compliance Operating System architecture:
 * - Disclosure Cycles (Hero)
 * - Frameworks (Library)
 * - Evidence Hub (Upload Once)
 * - Obligations Engine (What You Owe)
 * - Audit & Assurance (Defend Everywhere)
 * - Value Chain (External Parties)
 * - Intelligence (Insights)
 * - Reports & Export (Output)
 * - System (Config)
 */
export const NAVIGATION_CONFIG = [
  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARDS & ANALYTICS - EXECUTIVE VIEW (Enterprise Reporting Hub)
  // "What's our compliance status across all entities?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'dashboards-analytics',
    type: NavSectionType.PRIMARY,
    label: 'Dashboards & Analytics',
    description: 'Executive View',
    icon: LayoutDashboard,
    defaultOpen: false,
    isHero: false,
    overviewPath: '/disclosure-os',
    overviewLabel: 'Unified Dashboard',
    items: [
      { 
        id: 'wired-hub',
        path: '/wired-hub', 
        icon: Plug, 
        label: 'Wired Hub',
        description: '16 live user journeys',
        badge: { type: BadgeType.NEW, value: 'LIVE' },
      },
      { 
        id: 'board-dashboard',
        path: '/dashboards/board', 
        icon: LayoutDashboard, 
        label: 'Board Dashboard',
        description: 'Executive compliance overview',
        badge: { type: BadgeType.NEW, value: 'NEW' },
      },
      { 
        id: 'entity-management',
        path: '/dashboards/entities', 
        icon: Building2, 
        label: 'Entity Management',
        description: 'Corporate structure & hierarchy',
      },
      { 
        id: 'consolidation',
        path: '/dashboards/consolidation', 
        icon: GitMerge, 
        label: 'Consolidation',
        description: 'Cross-entity data aggregation',
        badge: { type: BadgeType.NEW, value: 'NEW' },
      },
    ],
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // DISCLOSURE CYCLES - PRIMARY WORKFLOW (Hero Section)
  // "What am I working on?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'disclosure-cycles',
    type: NavSectionType.PRIMARY,
    label: 'Disclosure Cycles',
    description: 'Primary Workflow',
    icon: FileText,
    defaultOpen: true,
    isHero: true,
    items: [
      { 
        id: 'active-cycles',
        path: '/cycles/active', 
        icon: FileText, 
        label: 'Active Cycles',
        badge: { type: BadgeType.COUNT, value: 3 },
        description: 'Your current disclosure cycles',
        // Sub-items would be dynamic cycles like:
        // - FY2024 CSRD/ESRS [In Evidence Review]
        // - 2025 CDP Climate [Mapping]
        // - Q1 2026 CSDDD Due Diligence [Upload]
      },
      { 
        id: 'upcoming-deadlines',
        path: '/cycles/deadlines', 
        icon: Calendar, 
        label: 'Upcoming Deadlines',
        description: 'Timeline view of all deadlines',
      },
      { 
        id: 'completed-disclosures',
        path: '/cycles/completed', 
        icon: CheckCircle2, 
        label: 'Completed Disclosures',
        description: 'Historical submissions',
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MATERIALITY INTELLIGENCE - UPSTREAM INTELLIGENCE LAYER
  // "What is material? What does it trigger?"
  // Framework-agnostic: feeds CSRD, GRI, ISSB, CDP, SASB downstream
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'materiality-intelligence',
    type: NavSectionType.MATERIALITY,
    label: 'Materiality Intelligence',
    description: 'Upstream Assessment',
    icon: Target,
    defaultOpen: false,
    items: [
      {
        id: 'iro-register',
        path: '/materiality-intelligence',
        icon: ClipboardList,
        label: 'IRO Register',
        description: 'Score impacts, risks & opportunities',
        badge: { type: BadgeType.NEW },
      },
      {
        id: 'materiality-connector',
        path: '/materiality-connector',
        icon: Shield,
        label: 'Materiality Connector',
        description: 'IRO-to-Framework Defensibility Flywheel',
      },
      {
        id: 'graph-intelligence',
        path: '/graph-intelligence',
        icon: Zap,
        label: 'Graph Intelligence',
        description: 'Neo4j master switch cascade',
        badge: { type: BadgeType.NEW, value: 'NEW' },
      },
      {
        id: 'materiality-thresholds',
        path: '/materiality-intelligence/thresholds',
        icon: SlidersHorizontal,
        label: 'Materiality Thresholds',
        description: 'Impact/financial cut-offs, entity → framework → default',
        badge: { type: BadgeType.NEW, value: 'NEW' },
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // FRAMEWORKS - REGULATION LIBRARY
  // "What are the requirements?"
  // Updated to match new Framework Taxonomy Architecture
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'frameworks',
    type: NavSectionType.LIBRARY,
    label: 'Frameworks',
    description: 'Regulation Library',
    icon: Scale,
    defaultOpen: false,
    // Top-level link to overview page
    overviewPath: '/frameworks',
    overviewLabel: 'All Frameworks',
    groups: [
      {
        id: 'mandatory-reporting',
        label: 'Mandatory Reporting',
        items: [
          { 
            id: 'csrd-esrs',
            path: '/frameworks/csrd-esrs-integrated', 
            icon: Scale, 
            label: 'CSRD / ESRS',
            description: 'EU Corporate Sustainability Reporting — consumes materiality',
            featured: true,
          },
          { 
            id: 'csddd',
            path: '/frameworks/csddd', 
            icon: Shield, 
            label: 'CSDDD',
            description: 'Corporate Sustainability Due Diligence',
          },
          {
            id: 'eu-taxonomy',
            path: '/frameworks/eu-taxonomy',
            icon: Leaf,
            label: 'EU Taxonomy',
            description: 'Sustainable activities classification',
          },
          {
            id: 'minimum-safeguards',
            path: '/frameworks/eu-taxonomy/safeguards',
            icon: ShieldCheck,
            label: 'Minimum Safeguards',
            description: 'Art. 18 OECD/UNGP alignment verdict',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'sfdr',
            path: '/frameworks/sfdr',
            icon: BarChart3,
            label: 'SFDR',
            description: 'Sustainable Finance Disclosure Regulation',
          },
          {
            id: 'sfdr-funds',
            path: '/frameworks/sfdr/funds',
            icon: Landmark,
            label: 'SFDR Funds',
            description: 'Art. 6/8/9 classification & Taxonomy disclosure',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          { 
            id: 'cbam',
            path: '/frameworks/cbam', 
            icon: Globe, 
            label: 'CBAM',
            description: 'Carbon Border Adjustment Mechanism',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          { 
            id: 'issb',
            path: '/frameworks/issb', 
            icon: TrendingUp, 
            label: 'ISSB',
            description: 'IFRS S1 + S2 investor-focused standards',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          { 
            id: 'governance',
            path: '/frameworks/governance', 
            icon: Landmark, 
            label: 'Governance & Ethics',
            description: 'ESRS G1 Board Oversight & Anti-Corruption',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
        ]
      },
      {
        id: 'esrs-topic-standards',
        label: 'ESRS Topic Standards',
        items: [
          {
            id: 'esrs-e2',
            path: '/frameworks/esrs/e2',
            icon: Factory,
            label: 'E2 Pollution',
            description: 'Air & water pollutants, substances of concern',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'esrs-e3',
            path: '/frameworks/esrs/e3',
            icon: Droplets,
            label: 'E3 Water & Marine',
            description: 'Withdrawal, consumption, stressed basins',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'esrs-e4',
            path: '/frameworks/esrs/e4',
            icon: TreePine,
            label: 'E4 Biodiversity',
            description: 'Sites, protected areas, MSA footprint',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'esrs-s2',
            path: '/frameworks/esrs/s2',
            icon: HardHat,
            label: 'S2 Value-Chain Workers',
            description: 'Supplier working-condition metrics',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'esrs-s3',
            path: '/frameworks/esrs/s3',
            icon: Users,
            label: 'S3 Communities',
            description: 'Affected communities, FPIC, grievances',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'esrs-s4',
            path: '/frameworks/esrs/s4',
            icon: ShoppingCart,
            label: 'S4 Consumers',
            description: 'Product incidents, complaints, recalls',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'esrs-e5',
            path: '/frameworks/esrs/e5',
            icon: Recycle,
            label: 'E5 Circular Economy',
            description: 'Resource inflows, waste, product outflows',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'esrs-s1',
            path: '/frameworks/esrs/s1',
            icon: Users,
            label: 'S1 Own Workforce',
            description: 'Headcount, training, gender pay gap',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
          {
            id: 'esrs-g1',
            path: '/frameworks/esrs/g1',
            icon: Gavel,
            label: 'G1 Business Conduct',
            description: 'Board register, lobbying & political influence',
            badge: { type: BadgeType.NEW, value: 'NEW' },
          },
        ]
      },
      {
        id: 'voluntary-commitments',
        label: 'Voluntary Commitments',
        items: [
          { 
            id: 'voluntary-frameworks',
            path: '/frameworks/voluntary', 
            icon: Globe, 
            label: 'Voluntary Frameworks',
            description: 'GRI, TCFD, CDP, SBTi, TNFD, SASB, GHG Protocol',
            featured: true,
          },
        ]
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EVIDENCE HUB - UPLOAD ONCE, USE EVERYWHERE
  // "Where is all my evidence?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'evidence-hub',
    type: NavSectionType.WORKFLOW,
    label: 'Evidence Hub',
    description: 'Upload Once',
    icon: FolderOpen,
    defaultOpen: true,
    items: [
      { 
        id: 'upload-ingest',
        path: '/evidence/idp', 
        icon: Upload, 
        label: 'Upload & Ingest',
        badge: { type: BadgeType.NEW },
        description: 'AI-powered document processing with OCR',
      },
      { 
        id: 'evidence-library',
        path: '/evidence/library', 
        icon: FolderOpen, 
        label: 'Evidence Library',
        description: 'Search across all evidence',
      },
      {
        id: 'cross-framework-map',
        path: '/evidence/cross-framework',
        icon: GitMerge,
        label: 'Cross-Framework Map',
        badge: { type: BadgeType.NEW },
        description: 'Where is this evidence used?',
      },
      {
        id: 'evidence-coverage',
        path: '/coverage',
        icon: ShieldCheck,
        label: 'Coverage',
        badge: { type: BadgeType.NEW },
        description: 'What can this entity defend this cycle?',
      },
      { 
        id: 'ownership-approval',
        path: '/evidence/ownership', 
        icon: UserCheck, 
        label: 'Ownership & Approval',
        description: 'Evidence approval workflows',
      },
      { 
        id: 'evidence-lineage',
        path: '/lineage', 
        icon: GitMerge, 
        label: 'Evidence Lineage',
        description: 'Trace data from source to report',
        badge: { type: BadgeType.NEW, value: 'NEW' },
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // OBLIGATIONS ENGINE - WHAT YOU OWE
  // "What do I need to do today?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'obligations-engine',
    type: NavSectionType.WORKFLOW,
    label: 'Obligations Engine',
    description: 'What You Owe',
    icon: Zap,
    defaultOpen: true,
    items: [
      { 
        id: 'action-queue',
        path: '/obligations/queue', 
        icon: Zap, 
        label: 'Action Queue',
        badge: { type: BadgeType.URGENT, value: 5 },
        description: 'Your tasks today',
        isGlobal: true, // Surfaces at top level
      },
      { 
        id: 'obligation-register',
        path: '/obligations/register', 
        icon: ClipboardList, 
        label: 'Obligation Register',
        description: 'All requirements across frameworks',
      },
      { 
        id: 'deadline-tracker',
        path: '/obligations/deadlines', 
        icon: Timer, 
        label: 'Deadline Tracker',
        description: 'Upcoming due dates',
      },
      { 
        id: 'gaps-risks',
        path: '/obligations/gaps', 
        icon: AlertTriangle, 
        label: 'Gaps & Risks',
        description: 'Missing data and risks',
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CLAIMS & ASSERTIONS
  // "What can I claim?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'claims-engine',
    type: NavSectionType.WORKFLOW,
    label: 'Claims Engine',
    description: 'Claims & Assertions',
    icon: FileText,
    defaultOpen: false,
    items: [
      { 
        id: 'claims-pipeline',
        path: '/operations/claims', 
        icon: FileText, 
        label: 'Claims Pipeline',
        badge: { type: BadgeType.NEW },
        description: 'Draft → Review → Publish claims',
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AUDIT & ASSURANCE - DEFEND EVERYWHERE
  // "How do I prove this?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'audit-assurance',
    type: NavSectionType.ASSURANCE,
    label: 'Audit & Assurance',
    description: 'Defend Everywhere',
    icon: Shield,
    defaultOpen: false,
    items: [
      { 
        id: 'audit-trail',
        path: '/audit/trail', 
        icon: Shield, 
        label: 'Audit Trail',
        description: 'Immutable activity log',
      },
      { 
        id: 'audit-packs',
        path: '/audit/packs', 
        icon: Package, 
        label: 'Audit Packs',
        description: 'One-click export packages',
      },
      { 
        id: 'assurance-readiness',
        path: '/audit/readiness', 
        icon: CheckSquare, 
        label: 'Assurance Readiness',
        description: 'Big 4 preparation status',
      },
      {
        id: 'methodology-transparency',
        path: '/audit/methodology',
        icon: CheckSquare,
        label: 'Methodology Transparency',
        description: 'Calculation derivations & inputs',
      },
      {
        id: 'evidence-lineage',
        path: '/audit/lineage',
        icon: Link2,
        label: 'Evidence Lineage',
        description: 'Claim <- Evidence <- Source',
      },
      {
        id: 'auditor-portal',
        path: '/auditor-portal',
        icon: Shield,
        label: 'Auditor Portal',
        description: 'External auditor verification workspace',
        badge: { type: BadgeType.NEW, value: 'NEW' },
      },
      {
        id: 'internal-controls',
        path: '/audit/internal-controls',
        icon: ListChecks,
        label: 'Internal Controls',
        description: 'GOV-5 control register & tests of control',
        badge: { type: BadgeType.NEW, value: 'NEW' },
      },
      {
        id: 'verifier-portal',
        path: '/audit/verifier',
        icon: FileSearch,
        label: 'Verifier Portal',
        description: 'ISAE 3000 engagements & traceability matrix',
        badge: { type: BadgeType.NEW, value: 'NEW' },
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // VALUE CHAIN - EXTERNAL PARTIES
  // "Who else is involved?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'value-chain',
    type: NavSectionType.EXTERNAL,
    label: 'Value Chain',
    description: 'External Parties',
    icon: Factory,
    defaultOpen: false,
    items: [
      { 
        id: 'value-chain-hub',
        path: '/value-chain', 
        icon: Factory, 
        label: 'Value Chain Hub',
        badge: { type: BadgeType.NEW, value: 'NEW' },
        description: 'Supplier map, surveys, Scope 3',
        featured: true,
      },
      { 
        id: 'supplier-portal',
        path: '/supplier-portal', 
        icon: Factory, 
        label: 'Supplier Portal',
        badge: { type: BadgeType.NEW },
        description: 'External self-service DD portal',
      },
      { 
        id: 'supplier-surveys',
        path: '/value-chain/supplier-portal/surveys', 
        icon: FileQuestion, 
        label: 'Supplier Surveys',
        description: 'Collect supplier ESG data',
      },
      { 
        id: 'scope3-collection',
        path: '/value-chain/scope3', 
        icon: Layers, 
        label: 'Scope 3 Data Collection',
        description: 'Value chain emissions',
      },
      { 
        id: 'stakeholder-engagement',
        path: '/value-chain/stakeholders', 
        icon: MessageSquare, 
        label: 'Stakeholder Engagement',
        description: 'External stakeholder management',
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // INTELLIGENCE - INSIGHTS
  // "What should I know?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'intelligence',
    type: NavSectionType.INSIGHTS,
    label: 'Intelligence',
    description: 'Insights',
    icon: BarChart3,
    defaultOpen: false,
    items: [
      { 
        id: 'ml-intelligence',
        path: '/intelligence/ml-intelligence', 
        icon: Brain, 
        label: 'Regulatory Intelligence',
        badge: { type: BadgeType.AI, value: 'AI' },
        description: 'Anomaly detection, conflict detection, audit readiness',
      },
      {
        id: 'regulatory-radar',
        path: '/intelligence/regulatory-radar',
        icon: Radar,
        label: 'Regulatory Radar',
        badge: { type: BadgeType.NEW, value: 'NEW' },
        description: 'Framework updates & horizon scanning',
      },
      {
        id: 'climate-scenarios',
        path: '/intelligence/climate-scenarios',
        icon: Thermometer,
        label: 'Climate Scenarios & VaR',
        badge: { type: BadgeType.NEW, value: 'NEW' },
        description: 'NGFS scenario analysis, physical & transition risk',
      },
      { 
        id: 'preflight-check',
        path: '/intelligence/preflight-check', 
        icon: ShieldCheck, 
        label: 'Pre-Flight Check',
        description: 'Audit readiness assessment',
      },
      { 
        id: 'analytics',
        path: '/intelligence/analytics', 
        icon: BarChart3, 
        label: 'Analytics',
        description: 'Performance metrics and trends',
      },
      { 
        id: 'ai-recommendations',
        path: '/intelligence/ai', 
        icon: ScanSearch, 
        label: 'AI Recommendations',
        badge: { type: BadgeType.AI },
        description: 'AI-powered insights',
      },
      { 
        id: 'dashboard',
        path: '/intelligence/dashboard', 
        icon: LayoutDashboard, 
        label: 'Dashboard',
        description: 'Executive overview',
      },
      {
        id: 'ai-intelligence',
        path: '/intelligence/ai-dashboard',
        icon: Cpu,
        label: 'AI Intelligence',
        badge: { type: BadgeType.AI, value: 'AI' },
        description: 'Central AI orchestration - 6 agents, 5 guardrail layers',
      },
      { 
        id: 'impact-simulation',
        path: '/simulations', 
        icon: FlaskConical, 
        label: 'Impact Simulation',
        badge: { type: BadgeType.NEW },
        description: 'Model what-if ESG scenarios & browse industry templates',
      },
      { 
        id: 'compare-scenarios',
        path: '/simulations/compare', 
        icon: GitCompareArrows, 
        label: 'Compare Scenarios',
        description: 'Side-by-side scenario analysis',
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REPORTS & EXPORT - OUTPUT
  // "What have we produced?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'reports-export',
    type: NavSectionType.OUTPUT,
    label: 'Reports & Export',
    description: 'Output',
    icon: FileBarChart,
    defaultOpen: false,
    items: [
      { 
        id: 'reports-hub',
        path: '/reports/hub', 
        icon: FileBarChart, 
        label: 'Reports Hub',
        badge: { type: BadgeType.NEW },
        description: 'Generate PDF reports for auditors',
      },
      { 
        id: 'published-reports',
        path: '/reports/published', 
        icon: FileBarChart, 
        label: 'Published Reports',
        description: 'Final disclosure documents',
      },
      { 
        id: 'export-center',
        path: '/reports/export', 
        icon: Download, 
        label: 'Export Center',
        description: 'Download data and reports',
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SYSTEM - CONFIGURATION (Bottom)
  // "How is the system configured?"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'system',
    type: NavSectionType.CONFIG,
    label: 'System',
    description: 'Configuration',
    icon: Settings,
    defaultOpen: false,
    isFooter: true,
    items: [
      { 
        id: 'data-connectors',
        path: '/system/connectors', 
        icon: Settings, 
        label: 'Data Connectors',
        description: 'ERP and data integrations',
      },
      { 
        id: 'access-control',
        path: '/system/access', 
        icon: Lock, 
        label: 'Access Control',
        description: 'Users, roles, permissions',
      },
      { 
        id: 'organization',
        path: '/system/organization', 
        icon: Building2, 
        label: 'Organization Settings',
        description: 'Company structure',
      },
      { 
        id: 'framework-selection',
        path: '/system/frameworks', 
        icon: ListChecks, 
        label: 'Framework Selection',
        description: 'Which regulations apply to us?',
      },
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ADMIN - INTERNAL ONLY
  // "Platform operations & monitoring"
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'admin',
    type: NavSectionType.ADMIN,
    label: 'Admin',
    description: 'Internal Operations',
    icon: MonitorCheck,
    defaultOpen: false,
    adminOnly: true,
    items: [
      { 
        id: 'system-status',
        path: '/admin/system-status', 
        icon: Gauge, 
        label: 'System Status',
        badge: { type: BadgeType.NEW },
        description: 'Real-time platform health monitoring',
      },
      { 
        id: 'user-management',
        path: '/admin/users', 
        icon: Lock, 
        label: 'User Management',
        description: 'Manage users and roles',
      },
      { 
        id: 'audit-logs',
        path: '/admin/audit-logs', 
        icon: ScrollText, 
        label: 'Audit Logs',
        description: 'Platform activity logs',
      },
    ]
  },
];

/**
 * Get navigation section by ID
 */
export function getNavigationSection(sectionId) {
  return NAVIGATION_CONFIG.find(section => section.id === sectionId);
}

/**
 * Get all items from a section (flattened if has groups)
 */
export function getSectionItems(sectionId) {
  const section = getNavigationSection(sectionId);
  if (!section) return [];
  
  if (section.groups) {
    return section.groups.flatMap(group => group.items);
  }
  return section.items || [];
}

/**
 * Get global action items (items marked as isGlobal)
 */
export function getGlobalActions() {
  const actions = [];
  NAVIGATION_CONFIG.forEach(section => {
    const items = section.items || [];
    items.forEach(item => {
      if (item.isGlobal) {
        actions.push({ ...item, sectionId: section.id });
      }
    });
  });
  return actions;
}

/**
 * Check if a path is active (exact or prefix match)
 * Uses exact match for paths that have sibling sub-paths in the nav
 */
export function isPathActive(currentPath, itemPath) {
  const currentBase = currentPath.split('?')[0];
  const itemBase = itemPath.split('?')[0];
  
  if (itemPath.includes('?')) {
    return currentPath === itemPath || currentPath.startsWith(itemPath);
  }

  // Exact match always wins
  if (currentBase === itemBase) return true;

  // For prefix matching, check that currentPath is truly a sub-path
  // but only if the item is not a parent whose children have their own nav entries
  // (e.g., /simulations should NOT highlight when on /simulations/templates)
  const siblingPaths = ['/simulations/compare', '/simulations/templates'];
  if (siblingPaths.some(sp => sp.startsWith(itemBase + '/')) && currentBase !== itemBase) {
    // This item has known siblings — only highlight on exact match
    return false;
  }

  return currentBase.startsWith(itemBase + '/');
}

/**
 * Badge styling configuration
 */
export const BADGE_STYLES = {
  [BadgeType.COUNT]: 'bg-emerald-500/20 text-emerald-400 min-w-[20px] text-center',
  [BadgeType.PHASE]: 'bg-blue-500/20 text-blue-400',
  [BadgeType.NEW]: 'bg-amber-500/20 text-amber-400',
  [BadgeType.AI]: 'bg-purple-500/20 text-purple-400',
  [BadgeType.URGENT]: 'bg-red-500/20 text-red-400 min-w-[20px] text-center',
  [BadgeType.BETA]: 'bg-gray-500/20 text-gray-400',
};

/**
 * Section type styling
 */
export const SECTION_STYLES = {
  [NavSectionType.PRIMARY]: {
    headerClass: 'text-emerald-400 border-emerald-500/30',
    accentClass: 'hover:text-emerald-400',
  },
  [NavSectionType.MATERIALITY]: {
    headerClass: 'text-amber-400 border-amber-500/30',
    accentClass: 'hover:text-amber-400',
  },
  [NavSectionType.LIBRARY]: {
    headerClass: 'text-blue-400 border-blue-500/30',
    accentClass: 'hover:text-blue-400',
  },
  [NavSectionType.WORKFLOW]: {
    headerClass: 'text-purple-400 border-purple-500/30',
    accentClass: 'hover:text-purple-400',
  },
  [NavSectionType.ASSURANCE]: {
    headerClass: 'text-amber-400 border-amber-500/30',
    accentClass: 'hover:text-amber-400',
  },
  [NavSectionType.EXTERNAL]: {
    headerClass: 'text-cyan-400 border-cyan-500/30',
    accentClass: 'hover:text-cyan-400',
  },
  [NavSectionType.INSIGHTS]: {
    headerClass: 'text-pink-400 border-pink-500/30',
    accentClass: 'hover:text-pink-400',
  },
  [NavSectionType.OUTPUT]: {
    headerClass: 'text-teal-400 border-teal-500/30',
    accentClass: 'hover:text-teal-400',
  },
  [NavSectionType.CONFIG]: {
    headerClass: 'text-[#6B6B6B] border-[#2F2F2F]',
    accentClass: 'hover:text-[#A0A0A0]',
  },
  [NavSectionType.ADMIN]: {
    headerClass: 'text-red-400 border-red-500/30',
    accentClass: 'hover:text-red-400',
  },
};

export default NAVIGATION_CONFIG;
